import { useEffect } from 'react';

import type { OverlapCheckGateRef } from '@app/components/Modals/BotDetailsModal/types';
import type { KeywordMessageConditionParameters } from '@app/components/Modals/BotDetailsModal/utils/keywordOverlaps';

import type { DebouncedFunc } from 'lodash';
import type { MutableRefObject } from 'react';

interface UseKeywordOverlapWarningEffectsProps {
  windowSessionId: number;
  shownSessionIdRef: MutableRefObject<number | null>;
  resetOverlapsView: () => void;
  conditionParameters: KeywordMessageConditionParameters;
  conditionsKey: string;
  latestConditionsKeyRef: MutableRefObject<string | null>;
  overlapCheckGateRef: OverlapCheckGateRef;
  debouncedCheck: DebouncedFunc<
    (
      parameters: KeywordMessageConditionParameters,
      sessionId: number | null
    ) => Promise<void>
  >;
}

export const useKeywordOverlapWarningEffects = ({
  windowSessionId,
  shownSessionIdRef,
  resetOverlapsView,
  conditionParameters,
  conditionsKey,
  latestConditionsKeyRef,
  overlapCheckGateRef,
  debouncedCheck,
}: UseKeywordOverlapWarningEffectsProps) => {
  // Проверка — через 500 мс после последнего изменения слов, канала,
  // интеграции, режима или выбора публикаций. Form.useWatch сохраняет
  // прежнюю ссылку, пока значение не изменилось, поэтому условия
  // пересчитываются только при настоящем изменении. Закрытое окно
  // проверку не шлёт: проверка запоминает сессию окна, и runCheck
  // отбрасывает вызов и ответ чужой сессии. windowSessionId в зависимостях:
  // каждое открытие окна запускает проверку, даже если черновик тот же.
  useEffect(() => {
    const { isAllowed, sessionId } = overlapCheckGateRef.current;
    const checkSessionId = isAllowed ? sessionId : null;

    // Окно закрыто или открыто заново: результат прошлой сессии скрывается
    if (
      checkSessionId === null ||
      checkSessionId !== shownSessionIdRef.current
    ) {
      resetOverlapsView();
    }

    shownSessionIdRef.current = checkSessionId;
    latestConditionsKeyRef.current = conditionsKey;
    debouncedCheck(conditionParameters, checkSessionId);
  }, [
    windowSessionId,
    shownSessionIdRef,
    resetOverlapsView,
    conditionParameters,
    conditionsKey,
    debouncedCheck,
    latestConditionsKeyRef,
    overlapCheckGateRef,
  ]);

  useEffect(() => () => debouncedCheck.cancel(), [debouncedCheck]);
};
